import React, { useState, useEffect, useCallback } from 'react';
import Button from '../ui/Button';
import {
    Shield, Users, Search, Loader2, Activity, ArrowLeft,
    ChevronRight, ToggleLeft, ToggleRight, X, Check, AlertCircle
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface HealthStatus {
    status: string;
    database?: string;
    timestamp?: string;
    uptime?: number;
    [key: string]: unknown;
}

interface AdminUser {
    id: string;
    email: string;
    subscription_tier: string;
    credits_remaining: number;
    is_active: boolean;
    created_at: string;
    api_key?: string;
    stripe_customer_id?: string;
    referral_code?: string;
}

interface CreditLog {
    id: string;
    amount: number;
    type: string;
    reason: string;
    balance_after: number;
    created_at: string;
}

const AdminPage: React.FC = () => {
    const [adminKey, setAdminKey] = useState('');
    const [authenticated, setAuthenticated] = useState(false);
    const [health, setHealth] = useState<HealthStatus | null>(null);
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
    const [userCredits, setUserCredits] = useState<CreditLog[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [editTier, setEditTier] = useState('');
    const [editCredits, setEditCredits] = useState('');
    const [editReason, setEditReason] = useState('');
    const [editActive, setEditActive] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState<string | null>(null);

    useEffect(() => {
        const stored = localStorage.getItem('progeodata_admin_key');
        if (stored) {
            setAdminKey(stored);
            setAuthenticated(true);
        }
    }, []);

    const authHeaders = useCallback(() => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${adminKey}`,
    }), [adminKey]);

    useEffect(() => {
        if (!authenticated) return;
        fetchHealth();
        fetchUsers(1);
    }, [authenticated]);

    const handleAuth = (e: React.FormEvent) => {
        e.preventDefault();
        if (!adminKey.trim()) return;
        localStorage.setItem('progeodata_admin_key', adminKey.trim());
        setAuthenticated(true);
    };

    const handleLogout = () => {
        localStorage.removeItem('progeodata_admin_key');
        setAdminKey('');
        setAuthenticated(false);
    };

    const fetchHealth = async () => {
        try {
            const res = await fetch(`${API_URL}/health`);
            if (res.ok) {
                const data = await res.json() as HealthStatus;
                setHealth(data);
            }
        } catch {}
    };

    const fetchUsers = async (p: number, query?: string) => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: String(p), limit: '20' });
            if (query || searchQuery) params.set('search', query || searchQuery);
            const res = await fetch(`${API_URL}/admin/users?${params}`, { headers: authHeaders() });
            if (!res.ok) {
                if (res.status === 401 || res.status === 403) {
                    handleLogout();
                    setError('Invalid admin key');
                    return;
                }
                throw new Error('Failed to fetch users');
            }
            const data = await res.json() as { users: AdminUser[]; total: number; page: number; totalPages: number };
            setUsers(data.users || []);
            setTotalPages(data.totalPages || 1);
            setPage(p);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchUserDetail = async (userId: string) => {
        try {
            const res = await fetch(`${API_URL}/admin/users/${userId}`, { headers: authHeaders() });
            if (!res.ok) throw new Error('Failed to fetch user');
            const data = await res.json() as { user: AdminUser; creditHistory?: CreditLog[] };
            setSelectedUser(data.user);
            setUserCredits(data.creditHistory || []);
            setEditTier(data.user.subscription_tier);
            setEditActive(data.user.is_active);
            setEditCredits('');
            setEditReason('');
            setSaveMessage(null);
        } catch (err: any) {
            setError(err.message);
        }
    };

    const handleSave = async () => {
        if (!selectedUser) return;
        setSaving(true);
        setSaveMessage(null);
        setError(null);
        try {
            const patchRes = await fetch(`${API_URL}/admin/users/${selectedUser.id}`, {
                method: 'PATCH',
                headers: authHeaders(),
                body: JSON.stringify({ tier: editTier, is_active: editActive }),
            });
            if (!patchRes.ok) throw new Error('Failed to update user');

            if (editCredits) {
                const creditsRes = await fetch(`${API_URL}/admin/users/${selectedUser.id}/credits`, {
                    method: 'POST',
                    headers: authHeaders(),
                    body: JSON.stringify({ amount: Number(editCredits), reason: editReason || 'Admin adjustment' }),
                });
                if (!creditsRes.ok) throw new Error('Failed to adjust credits');
            }

            setSaveMessage('Saved successfully');
            fetchUsers(page);
            fetchUserDetail(selectedUser.id);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        fetchUsers(1, searchQuery);
    };

    if (!authenticated) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
                <div className="max-w-md w-full">
                    <div className="text-center mb-8">
                        <Shield className="w-12 h-12 text-sky-500 mx-auto mb-4" />
                        <h1 className="text-3xl font-bold text-white mb-2">Admin Access</h1>
                        <p className="text-slate-400">Enter your admin key to continue</p>
                    </div>
                    <form onSubmit={handleAuth} className="bg-slate-800 border border-slate-700 rounded-2xl p-8">
                        {error && (
                            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                                {error}
                            </div>
                        )}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-slate-300 mb-2">Admin Key</label>
                            <input
                                type="password"
                                value={adminKey}
                                onChange={(e) => setAdminKey(e.target.value)}
                                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                                placeholder="Enter admin key"
                                autoFocus
                            />
                        </div>
                        <Button type="submit" variant="primary" className="w-full">
                            Authenticate
                        </Button>
                    </form>
                </div>
            </div>
        );
    }

    const healthColor = health?.status === 'ok' || health?.status === 'healthy'
        ? 'bg-emerald-500' : health?.status === 'degraded' ? 'bg-amber-500' : 'bg-red-500';

    const tierColors: Record<string, string> = {
        free: 'bg-slate-600',
        starter: 'bg-sky-600',
        growth: 'bg-emerald-600',
        agency: 'bg-amber-600'
    };

    return (
        <div className="min-h-screen bg-slate-900 py-12 px-4">
            <meta name="robots" content="noindex,nofollow" />
            <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                            <Shield className="w-8 h-8 text-sky-500" />
                            Admin Dashboard
                        </h1>
                        <p className="text-slate-400">System management & user administration</p>
                    </div>
                    <Button variant="outline" onClick={handleLogout}>
                        <ArrowLeft className="w-4 h-4 mr-2" /> Exit Admin
                    </Button>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <span>{error}</span>
                        <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                            <span className="text-slate-400 text-sm">API Health</span>
                            <Activity className="w-5 h-5 text-sky-500" />
                        </div>
                        <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${healthColor} animate-pulse`} />
                            <span className="text-white font-bold text-lg capitalize">{health?.status || 'Unknown'}</span>
                        </div>
                        {health?.database && (
                            <p className="text-slate-500 text-sm mt-2">Database: {String(health.database)}</p>
                        )}
                        {health?.uptime != null && (
                            <p className="text-slate-500 text-sm">Uptime: {Math.floor(Number(health.uptime) / 60)}m</p>
                        )}
                    </div>

                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                            <span className="text-slate-400 text-sm">Quick Actions</span>
                            <Shield className="w-5 h-5 text-slate-500" />
                        </div>
                        <div className="space-y-2">
                            <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => fetchUsers(1)}>
                                <Users className="w-4 h-4 mr-2" /> Refresh Users
                            </Button>
                            <Button variant="outline" size="sm" className="w-full justify-start" onClick={fetchHealth}>
                                <Activity className="w-4 h-4 mr-2" /> Refresh Health
                            </Button>
                        </div>
                    </div>

                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                            <span className="text-slate-400 text-sm">Timestamp</span>
                        </div>
                        <p className="text-white font-medium">{new Date().toLocaleString()}</p>
                        {health?.timestamp && (
                            <p className="text-slate-500 text-sm mt-2">Server: {new Date(health.timestamp).toLocaleString()}</p>
                        )}
                    </div>
                </div>

                <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 mb-8">
                    <div className="flex items-center gap-2 mb-4">
                        <Users className="w-5 h-5 text-sky-500" />
                        <h2 className="text-lg font-bold text-white">User Management</h2>
                    </div>

                    <form onSubmit={handleSearch} className="flex gap-3 mb-6">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                                placeholder="Search by email..."
                            />
                        </div>
                        <Button type="submit" variant="primary">Search</Button>
                    </form>

                    {loading ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
                        </div>
                    ) : users.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>No users found</p>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="text-left text-slate-400 text-sm border-b border-slate-700">
                                            <th className="pb-3">Email</th>
                                            <th className="pb-3">Tier</th>
                                            <th className="pb-3">Credits</th>
                                            <th className="pb-3">Active</th>
                                            <th className="pb-3">Created</th>
                                            <th className="pb-3"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map((u) => (
                                            <tr
                                                key={u.id}
                                                className="border-b border-slate-700/50 hover:bg-slate-700/30 cursor-pointer transition-colors"
                                                onClick={() => fetchUserDetail(u.id)}
                                            >
                                                <td className="py-3 text-slate-300 text-sm">{u.email}</td>
                                                <td className="py-3">
                                                    <span className={`px-2 py-1 rounded text-xs font-medium text-white ${tierColors[u.subscription_tier] || 'bg-slate-600'}`}>
                                                        {u.subscription_tier}
                                                    </span>
                                                </td>
                                                <td className="py-3 text-slate-300 text-sm">{u.credits_remaining.toLocaleString()}</td>
                                                <td className="py-3">
                                                    {u.is_active ? (
                                                        <span className="text-emerald-400 text-xs font-medium">Active</span>
                                                    ) : (
                                                        <span className="text-red-400 text-xs font-medium">Inactive</span>
                                                    )}
                                                </td>
                                                <td className="py-3 text-slate-500 text-sm">{new Date(u.created_at).toLocaleDateString()}</td>
                                                <td className="py-3"><ChevronRight className="w-4 h-4 text-slate-600" /></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {totalPages > 1 && (
                                <div className="flex items-center justify-center gap-2 mt-6">
                                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => fetchUsers(page - 1)}>Prev</Button>
                                    <span className="text-slate-400 text-sm">Page {page} of {totalPages}</span>
                                    <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => fetchUsers(page + 1)}>Next</Button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {selectedUser && (
                    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setSelectedUser(null)}>
                        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-bold text-white">User Detail</h3>
                                <button onClick={() => setSelectedUser(null)} className="text-slate-400 hover:text-white">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="space-y-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-slate-900 rounded-lg p-4">
                                        <span className="text-slate-500 text-xs">Email</span>
                                        <p className="text-white text-sm font-medium mt-1">{selectedUser.email}</p>
                                    </div>
                                    <div className="bg-slate-900 rounded-lg p-4">
                                        <span className="text-slate-500 text-xs">User ID</span>
                                        <p className="text-slate-300 text-xs font-mono mt-1 break-all">{selectedUser.id}</p>
                                    </div>
                                    <div className="bg-slate-900 rounded-lg p-4">
                                        <span className="text-slate-500 text-xs">Credits</span>
                                        <p className="text-white text-lg font-bold mt-1">{selectedUser.credits_remaining.toLocaleString()}</p>
                                    </div>
                                    <div className="bg-slate-900 rounded-lg p-4">
                                        <span className="text-slate-500 text-xs">Created</span>
                                        <p className="text-white text-sm mt-1">{new Date(selectedUser.created_at).toLocaleString()}</p>
                                    </div>
                                </div>

                                <div className="border-t border-slate-700 pt-6">
                                    <h4 className="text-white font-bold mb-4">Actions</h4>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Tier</label>
                                            <select
                                                value={editTier}
                                                onChange={(e) => setEditTier(e.target.value)}
                                                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                                            >
                                                <option value="free">Free</option>
                                                <option value="starter">Starter</option>
                                                <option value="growth">Growth</option>
                                                <option value="agency">Agency</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Adjust Credits (positive to add, negative to subtract)</label>
                                            <input
                                                type="number"
                                                value={editCredits}
                                                onChange={(e) => setEditCredits(e.target.value)}
                                                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                                                placeholder="0"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Reason</label>
                                            <input
                                                type="text"
                                                value={editReason}
                                                onChange={(e) => setEditReason(e.target.value)}
                                                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                                                placeholder="Admin adjustment"
                                            />
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-slate-300">Active Status</span>
                                            <button
                                                onClick={() => setEditActive(!editActive)}
                                                className="text-slate-400 hover:text-white"
                                            >
                                                {editActive ? (
                                                    <ToggleRight className="w-8 h-8 text-emerald-500" />
                                                ) : (
                                                    <ToggleLeft className="w-8 h-8 text-red-500" />
                                                )}
                                            </button>
                                        </div>

                                        {saveMessage && (
                                            <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-400 text-sm flex items-center gap-2">
                                                <Check className="w-4 h-4" /> {saveMessage}
                                            </div>
                                        )}

                                        <Button variant="primary" className="w-full" onClick={handleSave} disabled={saving}>
                                            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                            Save Changes
                                        </Button>
                                    </div>
                                </div>

                                {userCredits.length > 0 && (
                                    <div className="border-t border-slate-700 pt-6">
                                        <h4 className="text-white font-bold mb-4">Credit History</h4>
                                        <div className="overflow-x-auto">
                                            <table className="w-full">
                                                <thead>
                                                    <tr className="text-left text-slate-400 text-xs border-b border-slate-700">
                                                        <th className="pb-2">Date</th>
                                                        <th className="pb-2">Type</th>
                                                        <th className="pb-2">Reason</th>
                                                        <th className="pb-2 text-right">Amount</th>
                                                        <th className="pb-2 text-right">Balance</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {userCredits.map((c) => (
                                                        <tr key={c.id} className="border-b border-slate-700/50">
                                                            <td className="py-2 text-slate-400 text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
                                                            <td className="py-2 text-xs">
                                                                <span className={`px-2 py-0.5 rounded text-xs ${
                                                                    c.type === 'purchase' ? 'bg-emerald-500/20 text-emerald-400' :
                                                                    c.type === 'usage' ? 'bg-sky-500/20 text-sky-400' :
                                                                    'bg-slate-500/20 text-slate-400'
                                                                }`}>
                                                                    {c.type}
                                                                </span>
                                                            </td>
                                                            <td className="py-2 text-slate-500 text-xs">{c.reason}</td>
                                                            <td className={`py-2 text-right font-medium text-xs ${c.amount > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                                {c.amount > 0 ? '+' : ''}{c.amount}
                                                            </td>
                                                            <td className="py-2 text-right text-slate-300 text-xs">{c.balance_after}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminPage;
